import { alpha } from '@theme-ui/color'
import { ArticlesArrowComponent } from 'components/knowledge-base/Categories'
import ExternalLink from 'components/UI/ExternalLink'
import InternalLink from 'components/UI/InternalLink'
import Svg from 'components/UI/SVG'
import LogoutMobileIcon from 'images/logout-mobile.svg'
import Cookies from 'js-cookie'
import { Dispatch, ReactElement, SetStateAction, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { actions } from 'store/session'
import { Flex, Text, get } from 'theme-ui'
import useBreakpointIndex from 'theme/useBreakpointIndex'
import getEnglishStrings from 'utils/getEnglishStrings'
import { isDotNetDomain } from 'utils/transformProductionApiUrlTld'
import headerLinkIconMap from '../headerLinkIconMap'
import ToggleNavigationMenuButton from './ToggleNavigationMenuButton'

export default function ToggleNavigationMenuTray({
  header,
  setIsMobileNavMenuOpen,
  isMobileNavOpen,
}: {
  header: Queries.HeaderLinksQuery['directus']
  setIsMobileNavMenuOpen: Dispatch<SetStateAction<boolean>>
  isMobileNavOpen: boolean
}): ReactElement {
  const breakpointIndex = useBreakpointIndex()
  const isMobileOrTablet = breakpointIndex === 0 || breakpointIndex === 1
  const generalTranslationsData = getEnglishStrings(header?.general_translations?.strings)
  const headerLinks = header.header_links
  const { data } = useAppSelector(state => state.session)
  const dispatch = useAppDispatch()

  // TODO: remove this when we refactor trays to use a presenter component
  useEffect(() => {
    const body = document.getElementsByTagName('body')[0]
    if (body) {
      if (isMobileNavOpen && isMobileOrTablet) {
        const scrollY = window.scrollY
        body.classList.add('non-scrollable')
        // store the scroll position so we can return to it when the menu is closed
        localStorage.setItem('scrollY', scrollY.toString())
      } else {
        body.classList.remove('non-scrollable')
      }
    }
  }, [isMobileOrTablet, isMobileNavOpen])

  return (
    <Flex>
      {!isMobileNavOpen ? (
        <ToggleNavigationMenuButton
          isMobileNavOpen={isMobileNavOpen}
          setIsMobileNavOpen={setIsMobileNavMenuOpen}
        />
      ) : (
        <Flex
          sx={{
            height: '100%',
            width: '100%',
          }}
        >
          <Flex
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: 'transparent',
              zIndex: 9,
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          />
          <Flex
            sx={{
              display: ['flex', 'flex', 'none'],
              flexDirection: 'column',
              fontSize: '1.8rem',
              width: ['100%', '40rem'],
              height: '100%',
              padding: ['1.6rem', '2.4rem'],
              backgroundColor: alpha('bodyBG', 0.96),
              position: 'fixed',
              top: 0,
              right: 0,
              zIndex: 10,
              svg: {
                'g > path': {
                  fillOpacity: 1,
                },
              },
            }}
          >
            <Flex
              sx={{
                justifyContent: 'flex-end',
                marginLeft: [0, '-1.2rem'],
              }}
            >
              <ToggleNavigationMenuButton
                isMobileNavOpen={isMobileNavOpen}
                setIsMobileNavOpen={setIsMobileNavMenuOpen}
              />
            </Flex>
            {headerLinks?.map(headerLink => {
              const headerLinkStrings = getEnglishStrings(headerLink?.strings)

              if (
                (!data?.username && headerLink?.requiresAuth) ||
                (data?.username && headerLink?.slug === '/login')
              ) {
                return null
              }

              return headerLink.slug === '/features' ? (
                <ExternalLink key={headerLink?.slug} href={headerLink?.slug || ''}>
                  <NavLinkContent
                    title={headerLinkStrings?.name ?? ''}
                    slug={headerLink?.slug}
                    setIsMobileNavMenuOpen={setIsMobileNavMenuOpen}
                  />
                </ExternalLink>
              ) : (
                <InternalLink key={headerLink?.slug} href={headerLink?.slug || ''}>
                  <NavLinkContent
                    title={headerLinkStrings?.name ?? ''}
                    slug={headerLink?.slug}
                    setIsMobileNavMenuOpen={setIsMobileNavMenuOpen}
                  />
                </InternalLink>
              )
            })}
            {data?.user_id && (
              <Flex
                onClick={() => {
                  const tld = isDotNetDomain() ? 'net' : 'com'
                  Cookies.remove('ws_session_auth_hash', {
                    domain: `.windscribe.${tld}`,
                    path: '/',
                  })
                  dispatch(actions.clearSessionData())
                }}
              >
                <NavLinkContent
                  icon={LogoutMobileIcon}
                  title={generalTranslationsData?.logout ?? ''}
                  slug={''}
                  setIsMobileNavMenuOpen={setIsMobileNavMenuOpen}
                />
              </Flex>
            )}
            <Flex
              sx={{
                alignItems: 'flex-end',
                width: '100%',
                height: '100%',
                justifyContent: 'center',
              }}
            >
              <ExternalLink
                href="/download"
                sxx={{
                  width: '100%',
                  display: 'flex',
                  variant: 'buttons.neon',

                  justifyContent: 'center',
                }}
                data-testid="menuTray/download"
              >
                Get Windscribe
              </ExternalLink>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

interface NavLinkContentProps {
  setIsMobileNavMenuOpen: Dispatch<SetStateAction<boolean>>
  slug: string
  title: string
  icon?: string
}

const NavLinkContent = ({
  setIsMobileNavMenuOpen,
  slug,
  title,
  icon,
}: NavLinkContentProps): ReactElement => {
  return (
    <Flex
      onClick={() => {
        setIsMobileNavMenuOpen(false)
      }}
      sx={{
        marginTop: '0.8rem',
        width: '100%',
        borderBottom: '0.1rem solid',
        borderBottomColor: 'white10',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: theme => `${get(theme, 'transitions.default')}`,
        color: location.pathname === slug ? 'white' : 'white50',
        ':hover': {
          '.articles-arrow': {
            transform: 'translateX(1rem)',
            opacity: 1,
          },
          '.header-link-icon': {
            opacity: 1,
          },
          color: 'white',
        },
        '.articles-arrow, .header-link-icon': {
          transition: theme => `${get(theme, 'transitions.default')}`,
        },
      }}
    >
      <Flex sx={{ py: '1.6rem', gap: '1.6rem' }}>
        <Svg
          className="header-link-icon"
          svg={icon ?? headerLinkIconMap[slug || '']}
          sx={{ opacity: location.pathname === slug ? 1 : 0.5 }}
        />
        <Text sx={{ fontWeight: 'bold' }}>{title}</Text>
      </Flex>
      {location.pathname !== slug && (
        <ArticlesArrowComponent
          className="articles-arrow"
          sxx={{ width: '2.4rem', height: '2.4rem' }}
        />
      )}
    </Flex>
  )
}
